// The single source of truth for the five competition formats: identity
// (icon + tone pair, the same canonical pairs as CompetitionFormatCard /
// FrontendHeader crests), the explainer carousel slides (member steps plus
// admin-only steps flagged `admin: true`), and the marketing copy the
// /formats pages build on. Keys match the admin dashboard's format keys.

export const FORMAT_KEYS = ['league', 'americano', 'ladder', 'tournament', 'meet'];

export const formatExplainers = {
  league: {
    key: 'league',
    name: 'Box leagues',
    singular: 'box league',
    icon: 'mdi-table',
    tint: 'rgb(var(--v-theme-brandTint))',
    ink: 'rgb(var(--v-theme-primary))',
    inkHex: '#D32946',
    tintHex: '#FBEDF0',
    path: '/formats/box-leagues',
    tagline: 'Divisions of similar strength, play everyone, move up or down.',
    lead: 'The classic club league: everyone is grouped into small divisions (boxes) of similar strength, plays everyone in their box over a few weeks, and moves up or down when the cycle rolls over.',
    slides: [
      {
        icon: 'mdi-format-list-group',
        title: 'Boxes of similar strength',
        blurb: 'Everyone is placed in a division (a box) with players of a similar level, so every match matters and none are a walkover. Turn handicaps on and even a mixed box starts every match from a fair place.',
      },
      {
        icon: 'mdi-account-group',
        title: 'Play everyone in your box',
        blurb: 'Over the cycle you play one match against each team in your division. Singles or doubles, your club decides.',
      },
      {
        icon: 'mdi-calendar-clock',
        title: 'Schedule at your own pace',
        blurb: 'Propose a date in the app, your opponent accepts, and the match is booked. No fixed nights unless your club wants them.',
      },
      {
        icon: 'mdi-scoreboard',
        title: 'Record the score in seconds',
        blurb: 'Enter the result straight from the match. Your opponent or an admin approves it and the table updates instantly.',
      },
      {
        icon: 'mdi-table',
        title: 'Points build the table',
        blurb: "Wins earn points under the club's scoring system, with optional bonus points for completing all your matches.",
      },
      {
        icon: 'mdi-swap-vertical',
        title: 'Promotion and relegation',
        blurb: 'Finish top and move up a box next cycle; finish bottom and drop down. Every cycle is a fresh start.',
      },
      {
        icon: 'mdi-auto-fix',
        title: 'Create it in minutes',
        blurb: "The league form plus the cycle wizard: tick who's playing and the divisions are laid out for you.",
        admin: true,
      },
      {
        icon: 'mdi-autorenew',
        title: 'Rollover does the hard work',
        blurb: 'Dropouts, players waiting to be placed, promotion and relegation are all previewed and applied in one guided flow.',
        admin: true,
      },
      {
        icon: 'mdi-credit-card-outline',
        title: 'Entry fees, handled',
        blurb: 'Charge per team, per cycle with club payments. Billing, receipts and refunds included.',
        payments: true,
        admin: true,
      },
      {
        icon: 'mdi-card-account-details-outline',
        title: 'Members only, or a member price',
        blurb: 'Need a membership or pass to enter, or give holders a lower entry fee. Players without one are offered it on the way in.',
        admin: true,
      },
      {
        icon: 'mdi-timetable',
        title: 'The court comes with the match',
        blurb: 'At a club that takes bookings, suggesting a match time books the court for it, or holds it while the other side replies.',
      },
    ],
    why: [
      {
        title: 'Every match is competitive',
        text: 'Boxes keep levels close, so nobody coasts and nobody gets crushed, so the match list is full of matches worth playing.',
      },
      {
        title: 'Flexible by design',
        text: 'Players arrange their own matches inside the cycle window. Perfect for clubs where players play at different times.',
      },
      {
        title: 'A season that never ends',
        text: 'Cycles roll into each other with promotion and relegation, so there is always something to play for.',
      },
    ],
    sports: ['Squash', 'Tennis', 'Padel', 'Badminton', 'Table tennis', 'Racketball', 'Pool', 'Snooker', 'Darts'],
    forWho: 'Clubs that want a season-long competitive structure players can fit around their own diaries.',
  },

  americano: {
    key: 'americano',
    name: 'Americano leagues',
    singular: 'americano league',
    icon: 'mdi-account-switch',
    tint: '#F0EAFB',
    ink: '#7C3AED',
    inkHex: '#7C3AED',
    tintHex: '#F0EAFB',
    path: '/formats/americano-leagues',
    tagline: 'Enter solo, partner everyone once, points count for you.',
    lead: 'The social-but-competitive doubles format taking padel and tennis clubs by storm: you enter on your own, partner every player in your division exactly once, and every result counts towards your personal standing.',
    slides: [
      {
        icon: 'mdi-account',
        title: 'Enter as an individual',
        blurb: 'No fixed partner needed. You join on your own and the format does the pairing for you.',
      },
      {
        icon: 'mdi-view-grid-outline',
        title: 'Divisions of 4, 8 or 12',
        blurb: 'Small groups at your level. The sizes are fixed so the partner rotation works out perfectly.',
      },
      {
        icon: 'mdi-account-switch',
        title: 'Partner everyone once',
        blurb: 'Matches are generated up front on a whist rotation: you partner every player in your division once and face them twice.',
      },
      {
        icon: 'mdi-scoreboard',
        title: 'Doubles matches, personal points',
        blurb: 'Every result counts towards YOUR standing, so win with different partners to climb the table. With handicaps on, each pair plays off their combined start, so a mixed pairing is still a real match.',
      },
      {
        icon: 'mdi-table',
        title: 'Standings that reward winning',
        blurb: 'Ranked on match points with sets and games as tiebreakers, or exact set scores where every game won is a point.',
      },
      {
        icon: 'mdi-swap-vertical',
        title: 'You move, not a team',
        blurb: 'Promotion and relegation happen per player when the cycle rolls over. Climb into the division above on your own merit.',
      },
      {
        icon: 'mdi-auto-fix',
        title: 'Set up in one wizard',
        blurb: "Pick who's playing (anyone who joined by link is waiting for you) and every partner-rotating match is generated instantly.",
        admin: true,
      },
      {
        icon: 'mdi-account-convert',
        title: 'Life happens: swap players',
        blurb: 'A mid-cycle substitute takes over the seat and its remaining matches; results already played stay put.',
      admin: true,
      },
      {
        icon: 'mdi-credit-card-outline',
        title: 'Per-player entry fees',
        blurb: 'Charge each player per cycle with club payments, collected automatically. Members only, or a member price, when the club runs memberships.',
        payments: true,
        admin: true,
      },
    ],
    why: [
      {
        title: 'No partner? No problem',
        text: 'Players join alone, so nobody misses out because their partner is injured, away or retired to the bar.',
      },
      {
        title: 'The fairest doubles test',
        text: 'Everyone plays with AND against everyone, so the standings measure you, not your partner.',
      },
      {
        title: 'Zero admin between rounds',
        text: 'The whole schedule exists the moment the cycle starts: 4 players means 3 matches, 8 means 14, 12 means 33.',
      },
    ],
    sports: ['Padel', 'Tennis', 'Badminton', 'Pickleball'],
    forWho: 'Doubles-sport clubs that want a social format with a real competitive edge, and no pairing headaches.',
  },

  ladder: {
    key: 'ladder',
    name: 'Ladders',
    singular: 'ladder',
    icon: 'mdi-ladder',
    tint: '#E8F1FB',
    ink: '#2477C8',
    inkHex: '#2477C8',
    tintHex: '#E8F1FB',
    path: '/formats/ladders',
    tagline: 'Challenge above you, win, take their spot.',
    lead: 'The simplest competition there is: one ranked list of everyone at the club. Challenge someone above you, beat them, and take their place.',
    slides: [
      {
        icon: 'mdi-ladder',
        title: 'Everyone on one ladder',
        blurb: 'The whole club ranked top to bottom. Your position is your bragging rights.',
      },
      {
        icon: 'mdi-sword-cross',
        title: 'Challenge above you',
        blurb: 'Pick someone within range above you and throw down a challenge from their card.',
      },
      {
        icon: 'mdi-calendar-clock',
        title: 'Accept and schedule',
        blurb: 'They accept, you agree a time in the app, and the match is on. Challenges expire if ignored, so there\'s no hiding at the top.',
      },
      {
        icon: 'mdi-swap-vertical',
        title: 'Win to take their spot',
        blurb: 'Beat them and their place is yours. Your club chooses whether the two of you swap, or you go above and everyone between moves down one.',
      },
      {
        icon: 'mdi-fire',
        title: 'Stay active, keep climbing',
        blurb: 'The ladder never sleeps: regular challenges protect your spot and sharpen your game.',
      },
      {
        icon: 'mdi-scale-balance',
        title: 'Handicaps keep it close',
        blurb: 'Turn handicaps on and a challenge against someone stronger starts from a fair place. The app shows the start the moment the challenge is sent.',
      },
      {
        icon: 'mdi-auto-fix',
        title: 'Start one in minutes',
        blurb: 'Add players in ranked order, or share the invite link and place new players as they arrive, and the ladder runs itself from there.',
        admin: true,
      },
      {
        icon: 'mdi-tune',
        title: 'You set the rules',
        blurb: 'Challenge range, expiry windows and the scoring system are all yours to configure.',
        admin: true,
      },
      {
        icon: 'mdi-credit-card-outline',
        title: 'Optional entry fee',
        blurb: 'Charge to join the ladder with club payments, one fee per entrant. Members only, or a member price, when the club runs memberships.',
        payments: true,
        admin: true,
      },
    ],
    why: [
      {
        title: 'Runs itself',
        text: 'No match list to publish and no rounds to manage. Players drive the competition by challenging each other.',
      },
      {
        title: 'Always-on competition',
        text: 'There is no season and no waiting: the ladder is live every day of the year.',
      },
      {
        title: 'Perfect matchmaking',
        text: 'Challenge ranges mean you always play someone near your level, and always have a target above you.',
      },
    ],
    sports: ['Squash', 'Tennis', 'Racketball', 'Table tennis', 'Badminton', 'Pool', 'Snooker', 'Chess'],
    forWho: 'Clubs that want a zero-admin, always-on ranking their players can battle over year-round.',
  },

  tournament: {
    key: 'tournament',
    name: 'Tournaments',
    singular: 'tournament',
    icon: 'mdi-trophy',
    tint: 'rgb(var(--v-theme-warningTint))',
    ink: 'rgb(var(--v-theme-warning))',
    inkHex: '#DE870A',
    tintHex: '#FCF3E4',
    path: '/formats/tournaments',
    tagline: 'Brackets, group stages and one name on the trophy.',
    lead: 'The big occasion: a knockout bracket, or group stages feeding a finals bracket. Draws, seedings, semi-finals and a champion.',
    slides: [
      {
        icon: 'mdi-trophy-outline',
        title: 'Knockout or groups + knockout',
        blurb: 'A straight bracket, or round-robin groups that feed a finals bracket: the two classic tournament shapes.',
      },
      {
        icon: 'mdi-tournament',
        title: 'The draw',
        blurb: 'Teams are drawn into the bracket, with manual seeding when it matters. Then it is win or go home.',
      },
      {
        icon: 'mdi-scoreboard',
        title: 'Play your round',
        blurb: 'Results progress the bracket automatically. Winners advance, and knockouts never end in a draw.',
      },
      {
        icon: 'mdi-table',
        title: 'Group tables',
        blurb: 'In group stages the standings decide who goes through, so every group match counts.',
      },
      {
        icon: 'mdi-crown-outline',
        title: 'A champion is crowned',
        blurb: 'Semi-finals, a final, maybe a third-place playoff, and one name on the trophy.',
      },
      {
        icon: 'mdi-scale-balance',
        title: 'Handicaps keep it close',
        blurb: 'Turn handicaps on and every round starts from a fair place, with values that can adjust at the end of each stage.',
      },
      {
        icon: 'mdi-auto-fix',
        title: 'Create and manage teams',
        blurb: 'A guided form sets the shape; the team manager handles entries and seedings.',
        admin: true,
      },
      {
        icon: 'mdi-shuffle-variant',
        title: 'Generate the draw',
        blurb: 'One click builds (or rebuilds) the bracket, and group winners progress to the knockout when you say so.',
        admin: true,
      },
      {
        icon: 'mdi-credit-card-outline',
        title: 'Entry fees per team',
        blurb: 'Collect tournament entry fees with club payments, pay links included. Members only, or a member price, when the club runs memberships.',
        payments: true,
        admin: true,
      },
    ],
    why: [
      {
        title: 'A proper occasion',
        text: 'Nothing focuses a club like a bracket on the wall. Finals day gives everyone something to build towards.',
      },
      {
        title: 'Fits any field size',
        text: 'Straight knockout for a quick event, or groups + knockout so everyone gets several matches before the pressure rounds.',
      },
      {
        title: 'The bracket runs live',
        text: 'Results progress rounds automatically and everyone can follow the draw from their phone.',
      },
    ],
    sports: ['Tennis', 'Squash', 'Padel', 'Badminton', 'Table tennis', 'Darts', 'Pool', 'Foosball'],
    forWho: 'Clubs running championships, finals days and one-off events with a trophy at the end.',
  },

  meet: {
    key: 'meet',
    name: 'Meets',
    singular: 'meet',
    icon: 'mdi-handshake',
    tint: 'rgb(var(--v-theme-successTint))',
    ink: 'rgb(var(--v-theme-success))',
    inkHex: '#1E9E76',
    tintHex: '#E7F6F1',
    path: '/formats/meets',
    tagline: 'Recurring social sessions. RSVP, turn up, play.',
    lead: 'The club social night, organised: a recurring session players RSVP to date by date. Matches are made on the night, everyone plays, and paid sessions bill themselves.',
    slides: [
      {
        icon: 'mdi-handshake-outline',
        title: 'Social sessions, not a match list',
        blurb: 'A recurring club night: the Wednesday mixer, the Friday social. Come when you can.',
      },
      {
        icon: 'mdi-hand-wave-outline',
        title: 'Join once, RSVP per date',
        blurb: "Say you're in for the dates you can make. Spots are limited, so it's first come, first served.",
      },
      {
        icon: 'mdi-account-clock-outline',
        title: 'Full? Join the waitlist',
        blurb: 'When a spot opens, the next person is offered it and has a set time to accept.',
      },
      {
        icon: 'mdi-shuffle',
        title: 'Matches made on the night',
        blurb: 'Organisers pair everyone up on the night, with balanced games and nobody sits out for long.',
      },
      {
        icon: 'mdi-chart-line',
        title: 'Results still count',
        blurb: 'Scores are recorded like any match, and your meet stats build session after session.',
      },
      {
        icon: 'mdi-credit-card-outline',
        title: 'Pay per session',
        blurb: 'Paid meets charge after the session, never when you sign up. Cancellation windows follow club rules, and no-shows pay in full.',
        payments: true,
      },
      {
        icon: 'mdi-ticket-confirmation-outline',
        title: 'Or get the pass',
        blurb: 'A pass can cover the block of sessions: pass holders are in free, and the pass is offered beside Count me in when it works out cheaper.',
        payments: true,
      },
      {
        icon: 'mdi-auto-fix',
        title: 'Create with the wizard',
        blurb: 'Set the schedule (weekly or custom dates), capacity, fees and terms in one guided flow.',
        admin: true,
      },
      {
        icon: 'mdi-clipboard-check-outline',
        title: 'Run the night from one screen',
        blurb: 'One screen on the night: register arrivals, generate the matches, record results and finalise the session.',
        admin: true,
      },
      {
        icon: 'mdi-cash-sync',
        title: 'Billing handled',
        blurb: 'Sessions bill automatically after they finalise, with retries and refunds when you need them.',
        payments: true,
        admin: true,
      },
    ],
    why: [
      {
        title: 'Turn up and play',
        text: 'No commitment beyond the nights you fancy. Ideal for players who cannot promise a whole season.',
      },
      {
        title: 'The organiser stays social too',
        text: 'Registration, pairing, results and billing all run from one screen on the night, so hosting a night is not a second job.',
      },
      {
        title: 'Fair, fun games',
        text: 'Pairings made on the night from who is actually there, so balanced matches without the spreadsheet.',
      },
    ],
    sports: ['Padel', 'Tennis', 'Badminton', 'Squash', 'Table tennis', 'Pickleball'],
    forWho: 'Clubs with a social night to run, from casual mixers to paid coached sessions.',
  },
};

/**
 * Slides for a format, filtered to the audience:
 * 'players' — member steps only; 'organisers' — admin steps only (the
 * marketing pages' toggle); 'all' — everything (the in-app admin dialogs).
 */
export function explainerSlides(formatKey, audience = 'players', {payments = true} = {}) {
  const format = formatExplainers[formatKey];
  if (!format) return [];
  // Slides about entry fees / billing (`payments: true`) only make sense
  // where the competition actually charges (or, on admin surfaces, where the
  // club has payments enabled) — callers pass `payments: false` to drop them.
  const slides = payments ? format.slides : format.slides.filter(slide => !slide.payments);
  if (audience === 'all') return slides;
  if (audience === 'organisers') return slides.filter(slide => slide.admin);
  return slides.filter(slide => !slide.admin);
}

// Longer-form marketing content for the /formats pages and hub: descriptive
// prose ("what is" — crawlable SEO copy), FAQs (rendered + FAQPage JSON-LD)
// and the hub page's feature-row copy. Kept beside the slides so every
// format's story lives in one file.
export const formatMarketing = {
  league: {
    hub: {
      title: 'The club season, on autopilot',
      paragraphs: [
        'Drop your players into divisions of similar strength and every box gets a full round-robin. Each player faces everyone in their division over the cycle, arranging matches at times that suit them.',
        'Results update the live table the moment they are approved, and when the cycle ends the rollover wizard applies promotion and relegation for you. The season simply keeps going, and optional handicaps keep every match close along the way.',
      ],
      caption: 'Division 1 · live standings',
    },
    about: [
      'A box league (also called a division league or pyramid league) groups a club\'s players into small divisions (boxes) of four to eight entrants of similar ability. Everyone plays everyone in their box across a cycle of a few weeks, scheduling each match whenever suits both players. It is the most popular way to run ongoing competitive play at racket-sport clubs because every match is close, and nobody has to commit to a fixed night.',
      'On Tap Leagues the whole thing runs itself: players propose match times in the app, record scores in seconds, and watch the live table. When a cycle ends, promotion and relegation move players between boxes and a new cycle starts. The rollover wizard handles dropouts, new joiners and the reshuffle in one flow. Entry fees, bonus points and custom scoring systems are all optional extras.',
    ],
    handicaps: [
      'Handicaps let a division mix standards without the results being a foregone conclusion. Pick the scheme for your sport (points per game, shots, goals or owe-and-receive), give each entrant a value, and every match card shows exactly what each game starts at before a ball is hit.',
      'Leave adjustment on and the values look after themselves: a decisive win nudges the winner\'s handicap, a close one barely moves it, and a full history of every change is kept in the competition.',
    ],
    faqs: [
      {q: 'Do box leagues support handicaps?', a: 'Yes. Turn handicaps on in the league settings, choose the scheme for your sport, and set a value per entrant. The calculator shows what any pairing would start at. Values carry forward on rollover and can adjust automatically after each result.'},
      {q: 'How many players do I need for a box league?', a: 'Anything from eight upwards works well. Most clubs run boxes of five or six players each, so 20 players makes four healthy divisions. The cycle wizard lays the divisions out for you and you can even them up with a drag.'},
      {q: 'How long should a cycle last?', a: 'Four to six weeks is typical: long enough for everyone to play four or five matches at their own pace, short enough that the promotion and relegation battle stays exciting.'},
      {q: 'What happens if someone does not play their matches?', a: 'Unplayed matches simply score nothing (clubs often award bonus points for completing every match), and walkovers can be recorded when someone forfeits. At rollover the wizard makes it easy to drop inactive players or move them down.'},
      {q: 'Can a box league be for members only?', a: 'Yes. Under Entry requirements, choose Membership or pass needed and tick the types that count, or give holders a different entry price. Players without one are offered it on the invite page and come back to join once it is through.'},
      {q: 'Is it free to run a box league?', a: 'Yes, it is free to use, on the web and in the iOS and Android apps. When a club takes payments through Tap Leagues, 2% commission plus Stripe\'s fees comes out of each payment. No per-entry fee and no setup fee.'},
    ],
  },
  americano: {
    hub: {
      title: 'Enter solo, partner everyone',
      paragraphs: [
        'The americano flips doubles on its head: everyone enters as an individual, and the whist rotation pairs you with every player in your division exactly once, so the standings measure you, not your partner.',
        'Every match exists the moment the cycle starts, results count to your personal total, and promotion and relegation happen per player when the cycle rolls over. Optional handicaps give each pair a combined start, so mixed-strength pairings still make a match.',
      ],
      caption: 'Division 1 · per-player standings',
    },
    about: [
      'An americano league (sometimes called a winner-stays social league or team-of-the-day format) is a doubles competition you enter on your own. Players are grouped into divisions of exactly 4, 8 or 12, and a whist rotation generates the full schedule up front: you partner every other player in your division once, and play against each of them twice. Popular in padel and growing fast in tennis and pickleball, it is the fairest way to rank individuals in a doubles sport.',
      'Tap Leagues scores every match to the individual players: match points with sets and games as tiebreakers, or exact set scores where every game won counts on the table. Cycles roll over with per-player promotion and relegation, mid-cycle substitutes can take over a seat without losing any played results, and entry fees are charged per player per cycle when you want them.',
    ],
    handicaps: [
      'Handicaps work per player in an americano: each entrant carries their own value, and every pairing plays off the two combined. The match card shows the pair\'s start and each player\'s own handicap underneath, so a beginner partnered with the club\'s best still gets a real game.',
      'Turn adjustment on and each player\'s value moves with their own results, not their partner\'s, keeping the rotation fair as the cycle goes on.',
    ],
    faqs: [
      {q: 'Can an americano league be for members only?', a: 'Yes. Under Entry requirements, choose Membership or pass needed and tick the types that count, or give holders a different entry price per cycle.'},
      {q: 'Do americano leagues support handicaps?', a: 'Yes, per player. Each entrant has their own handicap and every pairing plays off the pair\'s combined start, which the app works out for you. The calculator lets you check any two-against-two before the round.'},
      {q: 'Why do americano divisions have to be 4, 8 or 12 players?', a: 'The whist rotation only resolves perfectly (everyone partnering everyone exactly once) at those sizes. Four players gives 3 matches over 3 rounds, eight gives 14 matches, and twelve gives 33.'},
      {q: 'Do I need a partner to join?', a: 'No, that is the point. You enter as an individual and the format gives you a different partner every round, so nobody misses out because their usual partner is away.'},
      {q: 'How are americano standings ranked?', a: 'By the points the scoring system awards, then set difference, then game difference. A champions tiebreak counts as one set and one game, so a tiebreak win is never worth more than a straight-sets win.'},
      {q: 'What happens if someone drops out mid-cycle?', a: 'An admin swaps in any club player: the newcomer inherits the seat and its remaining matches, while the leaver\'s played results and points stay on the board.'},
    ],
  },
  ladder: {
    hub: {
      title: 'Climb, defend, repeat',
      paragraphs: [
        'One ranked list of everyone at the club. See someone above you? Challenge them, agree a time in the app, and take their place if you win.',
        'Challenge ranges and optional handicaps keep matches close, expiry windows stop anyone hiding at the top, and the ladder runs all year with nothing for an organiser to do.',
      ],
      caption: 'Club ladder · live',
    },
    about: [
      'A challenge ladder is the oldest club competition there is: every player ranked on one list, with matches made by challenge rather than a published match list. You can challenge players within a set range above you; win and you take their place. Your club decides whether the two of you swap, or you go above and everyone between moves down one. Because there are no rounds or deadlines, a ladder never finishes. It is a permanent, living ranking of the club.',
      'Tap Leagues replaces the noticeboard: challenges are issued straight from the ladder table, expire automatically if ignored, and matches are scheduled and scored in the app. Admins set the challenge range, expiry window and scoring system once, and the ladder looks after itself from then on.',
    ],
    handicaps: [
      'Handicaps take the sting out of challenging above your level. Each player on the ladder carries a value, and when a challenge is sent both sides see the start (\'Owe 15\', \'6–0 up each game\', \'471 to start\') before anyone books a court.',
      'The chance-of-success estimate on a challenge takes the handicap into account, and with adjustment on, values move with results so a fast improver does not stay a bandit for long.',
    ],
    faqs: [
      {q: 'Can a ladder be for members only?', a: 'Yes. Under Entry requirements, choose Membership or pass needed and tick the types that count, or give holders a different entry fee. At a club that takes bookings, a challenge books its court as part of suggesting the time.'},
      {q: 'Do ladders support handicaps?', a: 'Yes. Set a value per player in the ladder settings and every challenge shows the resulting start when it is sent and again on the match page. Values can adjust automatically after each result.'},
      {q: 'How do players join an ongoing ladder?', a: 'New players join from the ladder\'s invite link and start at the bottom (straight away, or once the admin has placed them), then climb by challenging. Joining mid-season is never a problem because there is no season.'},
      {q: 'What stops the top player refusing challenges?', a: 'Challenges expire: ignore one past the club\'s window and the ladder can treat it as a loss or move them down, so sitting on a position is not an option.'},
      {q: 'Who can I challenge?', a: 'Anyone within the challenge range your club sets (commonly three to five places above you), which keeps matches competitive all the way up the ladder.'},
      {q: 'Can a ladder run alongside a box league?', a: 'Yes, and it is a great combination: the league gives structured cycles, the ladder soaks up spontaneous "fancy a game tonight?" matches, both from the same player list.'},
    ],
  },
  tournament: {
    hub: {
      title: 'Bracket day, live on every phone',
      paragraphs: [
        'Straight knockout for the drama, or group stages first so everyone gets a run of matches before the pressure rounds. Seed the draw, generate the bracket, and let the day run itself.',
        'Results progress rounds automatically and the whole club can follow the bracket live, right through to the final. Optional handicaps keep early rounds competitive when standards vary.',
      ],
      caption: 'Semi-finals · live bracket',
    },
    about: [
      'A tournament is the club\'s big occasion: a field of entrants, a draw, and rounds that knock players out until a champion remains. Tap Leagues runs the two classic shapes (straight knockout, and group stages feeding a knockout) for any head-to-head sport, with optional seeding and a third-place playoff.',
      'Organisers manage entries in a team manager, generate (and regenerate) the draw with one click, and progress group winners into the knockout when the tables are settled. Results advance the bracket automatically, knockout matches can never end in a draw, and everyone follows the bracket live from their phone.',
    ],
    handicaps: [
      'Handicaps let a club championship welcome every standard. Each entrant carries a value, every match card shows what the round starts at, and the group matrix shows the handicaps next to the names so nobody is surprised on the day.',
      'Choose whether values adjust after each match or only at the end of a stage, so a group stage can settle before the knockout rounds begin.',
    ],
    faqs: [
      {q: 'Can a tournament be for members only?', a: 'Yes. Under Entry requirements, choose Membership or pass needed and tick the types that count, or give holders a different entry fee. Both players in a pair need a ticked membership.'},
      {q: 'Do tournaments support handicaps?', a: 'Yes, for both knockout and groups-then-knockout draws. Turn handicaps on in the tournament settings, give each entrant a value, and every match shows its start. Adjustment can run after each match or at the end of each stage.'},
      {q: 'Knockout or groups + knockout: which should I run?', a: 'Straight knockout suits a one-day event with limited playing time. Groups + knockout guarantees everyone several matches and makes finals day a genuine climax, and the usual pick for club championships.'},
      {q: 'Can I seed the draw?', a: 'Yes. Mark manual seeds in the team manager before generating, and the draw keeps the top seeds apart until the later rounds.'},
      {q: 'How many entrants do I need?', a: 'Four upwards for a knockout; groups + knockout shines from about eight. Byes fill any gaps automatically, so awkward numbers are fine.'},
      {q: 'Does it work for doubles and teams?', a: 'Yes. Entries are teams of any size, so singles, doubles or full team events all run the same way.'},
    ],
  },
  meet: {
    hub: {
      title: 'Club night, organised',
      paragraphs: [
        'Meets turn a recurring social night into a one-tap RSVP: players join once, say yes to the dates they can make, and when someone drops out the next on the waitlist is offered the spot.',
        'On the night the organiser registers arrivals, pairs everyone up, and records results from one screen, with paid sessions billing themselves after play.',
      ],
      caption: 'Wednesday mixer · next dates',
    },
    about: [
      'A meet is a recurring club social session (the Wednesday mixer, the Friday night social) run with per-date RSVPs instead of a match list. Players join the meet once, then simply say they are in for the dates they can make. Capacity caps, waitlists that offer each freed spot in turn, and cancellation windows keep every session fair without the organiser chasing anyone.',
      'On the night, one screen handles everything: register who actually turned up, generate balanced matches from the room, record scores and finalise. Results feed player stats like any other match, and paid meets charge attendees only after they have played, with the billing, retries and refunds handled by club payments.',
    ],
    faqs: [
      {q: 'Can a meet be for members only?', a: 'Yes. Under Entry requirements, choose Membership or pass needed and tick the types that count, or give holders a cheaper session price. The requirement is checked on each date a player books.'},
      {q: 'Can a pass cover the sessions?', a: 'Yes. A pass with repeating blocks can cover a meet: its holders are in free on each date, everyone else pays the session fee, and the pass is offered beside Count me in when it works out cheaper than paying per session.'},
      {q: 'How is a meet different from a league?', a: 'There is no table to climb and no commitment: a meet is session-based social play. Come to the dates you fancy, play balanced games, and your stats still build over time.'},
      {q: 'What happens when a session is full?', a: 'New RSVPs join the waitlist. If someone cancels, the first waitlisted player is offered the spot and has a set time to accept. No group-chat scramble.'},
      {q: 'When do players pay for a paid meet?', a: 'Only after they play: sessions bill when the organiser finalises the night, and late cancellations follow the club\'s cancellation window rules.'},
      {q: 'Who makes the matches on the night?', a: 'The organiser, from one screen on the night. It generates balanced pairings from exactly who is in the room, so nobody sits out for long.'},
    ],
  },
};
